import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import './ContactForm.css';
import Spinner from './Spinner';
import Notification from './Notification';
import { useTracking } from '../hooks/useTracking';

const ContactForm = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ 
    message: '', 
    show: false, 
    type: 'success' 
  });

  const { executeRecaptcha } = useGoogleReCaptcha();
  const { trackLead } = useTracking();

  const showNotification = (message, type = 'error') => {
    setNotification({ message, show: true, type });
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Generar token reCAPTCHA al momento del envío
      let recaptchaToken = null;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('submit_contact');
          console.log('reCAPTCHA token generated successfully');
        } catch (error) {
          console.error('Error generating reCAPTCHA token:', error);
          showNotification('Error de reCAPTCHA, por favor intente nuevamente.', 'error');
          setIsLoading(false);
          return;
        }
      }

      // Validar email
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        showNotification('Dirección de correo electrónico no válida.', 'error');
        setIsLoading(false);
        return;
      }

      // Validaciones básicas
      if (!name.trim() || !email.trim() || !message.trim()) {
        showNotification('Por favor, complete todos los campos.', 'warning');
        setIsLoading(false);
        return;
      }

      // Verificar reCAPTCHA si tenemos token
      let recaptchaValid = true;
      if (recaptchaToken) {
        try {
          const response = await fetch(process.env.REACT_APP_VERIFY_RECAPTCHA_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token: recaptchaToken }),
          });

          if (response.ok) {
            const result = await response.json();
            recaptchaValid = result.success;
            console.log('reCAPTCHA verification:', result);
          } else {
            console.warn('reCAPTCHA verification failed, but continuing...');
          }
        } catch (error) {
          console.warn('reCAPTCHA server not available, continuing without verification:', error);
        }
      }

      if (!recaptchaValid) {
        showNotification('Error de verificación reCAPTCHA. Por favor, intente nuevamente.', 'error');
        setIsLoading(false);
        return;
      }

      // Preparar datos del contacto
      const contactData = {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        timestamp: new Date(),
        recaptchaToken: recaptchaToken || 'no-token',
        isArchived: false,
        status: 'nuevo',
        userAgent: navigator.userAgent,
        ip: 'unknown',
        emailsSent: false, // Se actualizará cuando las Cloud Functions envíen los emails
        customerEmailSent: false,
        adminEmailSent: false
      };

      console.log('Saving contact to Firestore...');

      // Guardar en Firestore - esto disparará automáticamente los emails
      const docRef = await addDoc(collection(db, 'contacts'), contactData);
      console.log('Contact saved with ID:', docRef.id);

      // Limpiar formulario
      setName('');
      setEmail('');
      setMessage('');
      trackLead();
      showNotification('¡Mensaje enviado correctamente! Recibirás una confirmación por email y te contactaremos pronto.', 'success');

    } catch (error) {
      console.error('Error al enviar el mensaje:', error);
      
      if (error.code === 'permission-denied') {
        showNotification('Error de permisos. Por favor, contacte al administrador.', 'error');
      } else if (error.message.includes('network')) {
        showNotification('Error de conexión. Verifique su internet e intente nuevamente.', 'error');
      } else {
        showNotification('Error al enviar el mensaje. Por favor, intente nuevamente o contacte por email.', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
  };

  if (isLoading) {
    return (
      <div className="contact-form">
        <Spinner />
        <p className="contact-form__loading-text">Enviando mensaje…</p>
      </div>
    );
  }

  return (
    <div className="contact-form">
      <h1 className="title-gradient-lm contact-form__title">Contacto</h1>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Nombre *</label>
          <input 
            type="text" 
            id="name" 
            value={name} 
            onChange={handleInputChange(setName)}
            maxLength={100}
            required 
            placeholder="Ingrese su nombre completo"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input 
            type="email" 
            id="email" 
            value={email} 
            onChange={handleInputChange(setEmail)}
            maxLength={100}
            required 
            placeholder="su.email@ejemplo.com"
          />
        </div>

        <div className="form-group">
          <label htmlFor="message">Mensaje *</label>
          <textarea 
            id="message" 
            value={message} 
            onChange={handleInputChange(setMessage)}
            required
            maxLength={1000}
            rows={5}
            placeholder="Escriba su mensaje aquí..."
            style={{ resize: 'vertical', minHeight: '100px' }}
          />
          <small className="contact-form__char-count">
            {message.length}/1000 caracteres
          </small>
        </div>

        <button
          type="submit"
          className="contact-form__submit"
          disabled={isLoading || !name.trim() || !email.trim() || !message.trim()}
        >
          {isLoading ? 'Enviando...' : 'Enviar Mensaje'}
        </button>
      </form>

      <div className="contact-form__footer">
        <p>* Campos obligatorios</p>
        <p>Sus datos serán tratados de forma confidencial y solo se utilizarán para responder a su consulta.</p>

        <div className="contact-form__tip" role="note">
          Al enviar tu mensaje, recibirás automáticamente una confirmación por email y nuestro equipo será notificado para contactarte.
        </div>
      </div>

      {/* Componente de notificación centrada */}
      <Notification
        message={notification.message}
        show={notification.show}
        type={notification.type}
        onClose={hideNotification}
        duration={5000}
      />
    </div>
  );
};

export default ContactForm;